import { useMutation } from '@tanstack/react-query'
import type { PreviewLaunch } from '../api/questions'

/**
 * Opens a preview in a new tab.
 *
 * The API mints the URL, so no screen here holds a hostname for the Simulator — the Admin
 * Site's whole configuration is five keys, and a sixth would have to be added to the deploy
 * workflow in the same commit. A new tab because that is the point: the paper on one screen,
 * the Simulator on the other.
 *
 * **The tab is opened before the URL is known, and that is deliberate.** A browser allows
 * `window.open` only while it can still see the gesture that caused it — a click, or a drop;
 * awaiting the API first spends that permission, and the tab is then blocked as a pop-up,
 * silently, with nothing on screen to say why. So the tab is opened empty during the gesture
 * and sent to the Simulator once the token comes back.
 *
 * It takes the call rather than the ids because two screens mint a preview from two different
 * things — a selection of stored Questions, and one file that is not stored at all — and this
 * awkward piece of browser knowledge is the only part they share.
 */
export function useOpenInSimulator<T>(mint: (input: T) => Promise<PreviewLaunch | null>) {
  return useMutation({
    mutationFn: async (input: T) => {
      const tab = window.open('', '_blank')

      try {
        const launch = await mint(input)

        // No launch means the API read the input and would not open it — a refused file.
        // The tab was opened on the gesture and has to be given back.
        if (launch === null) {
          tab?.close()
          return null
        }

        if (tab) {
          // The handle is needed to navigate it, so `noopener` cannot be passed; cutting
          // the link by hand does the same job before the Simulator ever loads.
          tab.opener = null
          tab.location.replace(launch.url)
        } else {
          // Blocked anyway. Worth one direct attempt, which some browsers allow.
          window.open(launch.url, '_blank', 'noopener,noreferrer')
        }

        return launch
      } catch (refused) {
        tab?.close()
        throw refused
      }
    },
  })
}
